import { useEffect, useId, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Tag } from '@joker/contracts'
import { useTRPC } from '../trpc'

/**
 * Choose a tag from the managed list. Typing filters it; when the text matches
 * no tag, the last line offers to create it, which makes it and picks it in one
 * go, without leaving the page. Not free text: a near-miss spelling finds the
 * existing tag first, and the server folds case and spacing so one idea is one tag.
 *
 * Keyboard: ↑ ↓ move, Enter picks, Escape closes.
 */
export function TagPicker({
  onPick,
  exclude = [],
  allowCreate = true,
  label = 'Add tag',
  disabled = false,
}: {
  onPick: (tag: Tag) => void
  /** Tag ids not to offer, such as those the person already carries. */
  exclude?: string[]
  allowCreate?: boolean
  label?: string
  disabled?: boolean
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const tags = useQuery(trpc.tags.list.queryOptions())
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const listId = useId()
  const create = useMutation(
    trpc.tags.create.mutationOptions({
      onSuccess: (t) => {
        void qc.invalidateQueries({ queryKey: trpc.tags.list.queryKey() })
        pick(t)
      },
    }),
  )

  /* Closes when focus or a click goes elsewhere. */
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
  const typed = norm(text)
  const options = (tags.data ?? []).filter((t) => !exclude.includes(t.id) && (!typed || norm(t.name).includes(typed)))
  const exact = (tags.data ?? []).some((t) => norm(t.name) === typed)
  const canCreate = allowCreate && typed.length > 0 && !exact
  const count = options.length + (canCreate ? 1 : 0)

  function pick(t: Tag) {
    onPick(t)
    setText('')
    setOpen(false)
    setActive(0)
  }
  const choose = (i: number) => {
    if (i < options.length) pick(options[i]!)
    else if (canCreate) create.mutate({ name: text })
  }

  return (
    <div className="tagpick" ref={box} onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        placeholder={`${label}…`}
        value={text}
        disabled={disabled || create.isPending}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((a) => Math.min(a + 1, Math.max(0, count - 1)))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            if (count) choose(Math.min(active, count - 1))
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open && (
        <ul className="tagpick-list" role="listbox" id={listId}>
          {options.map((t, i) => (
            <li
              key={t.id}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'on' : undefined}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(t)
              }}
            >
              {t.name} <span className="muted">· {t.people}</span>
            </li>
          ))}
          {canCreate && (
            <li
              role="option"
              aria-selected={active === options.length}
              className={`tagpick-create${active === options.length ? ' on' : ''}`}
              onMouseEnter={() => setActive(options.length)}
              onMouseDown={(e) => {
                e.preventDefault()
                choose(options.length)
              }}
            >
              + Create tag “{text.trim().replace(/\s+/g, ' ')}”
            </li>
          )}
          {count === 0 && <li className="muted tagpick-empty">{tags.data ? 'No tags yet. Type a name to create one.' : 'Loading…'}</li>}
        </ul>
      )}
      {create.error && <div className="bad-text">{create.error.message}</div>}
    </div>
  )
}

/** A person's tags as chips with ×, plus the picker. */
export function PersonTags({ personId }: { personId: string }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const mine = useQuery(trpc.tags.forPerson.queryOptions({ personId }))
  const after = () => {
    void qc.invalidateQueries({ queryKey: trpc.tags.forPerson.queryKey({ personId }) })
    void qc.invalidateQueries({ queryKey: trpc.tags.list.queryKey() })
  }
  const add = useMutation(trpc.tags.add.mutationOptions({ onSuccess: after }))
  const remove = useMutation(trpc.tags.remove.mutationOptions({ onSuccess: after }))
  const list = mine.data ?? []
  return (
    <div className="person-tags">
      <span className="person-tags-k">Tags</span>
      {list.map((t) => (
        <span key={t.id} className="tagchip" title={t.addedBy ? `Added by ${t.addedBy}` : undefined}>
          {t.name}
          <button
            type="button"
            aria-label={`Remove tag ${t.name}`}
            disabled={remove.isPending}
            onClick={() => remove.mutate({ tagId: t.id, personIds: [personId] })}
          >
            ×
          </button>
        </span>
      ))}
      <TagPicker exclude={list.map((t) => t.id)} onPick={(t) => add.mutate({ tagId: t.id, personIds: [personId] })} disabled={add.isPending} />
    </div>
  )
}

/** The bar that tags or untags everybody ticked in a list. */
export function BulkTagBar({ selected, onDone }: { selected: string[]; onDone: () => void }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [result, setResult] = useState<string | null>(null)
  const after = (verb: string, name: string) => (r: { changed: number }) => {
    void qc.invalidateQueries()
    setResult(`${verb} “${name}” ${verb === 'Tagged' ? 'on' : 'from'} ${r.changed} ${r.changed === 1 ? 'person' : 'people'}.`)
    onDone()
  }
  const add = useMutation(trpc.tags.add.mutationOptions())
  const remove = useMutation(trpc.tags.remove.mutationOptions())
  if (!selected.length) return result ? <p className="footnote">{result}</p> : null
  const n = selected.length
  return (
    <div className="bulkbar" onClick={(e) => e.stopPropagation()}>
      <b>{n} selected</b>
      <span>Tag selected:</span>
      <TagPicker
        label="Tag selected"
        disabled={add.isPending}
        onPick={(t) => add.mutate({ tagId: t.id, personIds: selected }, { onSuccess: after('Tagged', t.name) })}
      />
      <span>Untag selected:</span>
      <TagPicker
        label="Untag selected"
        allowCreate={false}
        disabled={remove.isPending}
        onPick={(t) => remove.mutate({ tagId: t.id, personIds: selected }, { onSuccess: after('Removed', t.name) })}
      />
      <button type="button" className="btn ghost" onClick={onDone}>
        Clear selection
      </button>
      {(add.error ?? remove.error) && <span className="bad-text">{(add.error ?? remove.error)!.message}</span>}
    </div>
  )
}
